import { useForm, Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Company, CompanyFormData } from "@/hooks/useCompanies";
import { TradeSpecialtiesSelector } from "@/components/partners/TradeSpecialtiesSelector";
import { UnionStatusSelector } from "@/components/partners/UnionStatusSelector";
import { ContractorPortalUsersSection } from "@/components/partners/ContractorPortalUsersSection";
import { RatingDisplay } from "@/components/partners/RatingDisplay";
import { PrequalificationStatusBadge } from "@/components/partners/PrequalificationStatusBadge";
import { usePartnerSettings } from "@/hooks/usePartnerSettings";
import { Info, Building2, Wrench, Users } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface CompanyFormProps {
  company?: Company;
  onSubmit: (data: CompanyFormData) => Promise<void>;
  onCancel: () => void;
}

export function CompanyForm({ company, onSubmit, onCancel }: CompanyFormProps) {
  const { settings } = usePartnerSettings();
  const { register, handleSubmit, setValue, watch, control, formState: { errors, isSubmitting } } = useForm<CompanyFormData>({
    defaultValues: company ? {
      name: company.name,
      industry: company.industry || '',
      website: company.website || '',
      phone: company.phone || '',
      email: company.email || '',
      address_line1: company.address_line1 || '',
      address_line2: company.address_line2 || '',
      city: company.city || '',
      state: company.state || '',
      zip_code: company.zip_code || '',
      country: company.country || 'USA',
      company_type: company.company_type,
      status: company.status,
      annual_revenue: company.annual_revenue || undefined,
      employee_count: company.employee_count || undefined,
      founded_year: company.founded_year || undefined,
      relationship_tier: company.relationship_tier || 'standard',
      notes: company.notes || '',
      // Contractor/Vendor fields
      trade_specialties: company.trade_specialties || [],
      service_area_states: company.service_area_states || [],
      service_area_radius_miles: company.service_area_radius_miles || undefined,
      license_number: company.license_number || '',
      license_state: company.license_state || '',
      license_expiry_date: company.license_expiry_date || '',
      license_type: company.license_type || '',
      insurance_general_liability_amount: company.insurance_general_liability_amount || undefined,
      insurance_workers_comp_amount: company.insurance_workers_comp_amount || undefined,
      insurance_umbrella_amount: company.insurance_umbrella_amount || undefined,
      insurance_expiry_date: company.insurance_expiry_date || '',
      insurance_carrier: company.insurance_carrier || '',
      bond_capacity: company.bond_capacity || undefined,
      bonding_company: company.bonding_company || '',
      is_mwbe_certified: company.is_mwbe_certified || false,
      certifications: company.certifications || [],
      duns_number: company.duns_number || '',
      federal_tax_id: company.federal_tax_id || '',
      union_status: company.union_status || undefined,
      payment_terms: company.payment_terms || '',
      typical_crew_size: company.typical_crew_size || undefined,
      years_in_business: company.years_in_business || undefined,
      prequalification_date: company.prequalification_date || '',
      prequalification_expiry_date: company.prequalification_expiry_date || '',
      last_project_date: company.last_project_date || '',
      contractor_notes: company.contractor_notes || '',
    } : {
      name: '',
      company_type: 'client',
      status: 'active',
      country: 'USA',
      relationship_tier: 'standard',
      trade_specialties: [],
      service_area_states: [],
      certifications: [],
      is_mwbe_certified: false,
      license_expiry_date: '',
      insurance_expiry_date: '',
      prequalification_date: '',
      prequalification_expiry_date: '',
      last_project_date: '',
    }
  });

  const companyType = watch('company_type');
  const status = watch('status');
  const relationshipTier = watch('relationship_tier');
  const tradeSpecialties = watch('trade_specialties') || [];
  const isMwbeCertified = watch('is_mwbe_certified');
  const unionStatus = watch('union_status');

  const isContractorOrVendor = companyType === 'contractor' || companyType === 'vendor';

  // Field visibility logic based on tenant settings
  const getEnabledFields = () => {
    if (!isContractorOrVendor || !settings?.field_config) return [];
    return settings.field_config[companyType]?.enabled_fields || [];
  };

  const getRequiredFields = () => {
    if (!isContractorOrVendor || !settings?.field_config) return [];
    return settings.field_config[companyType]?.required_fields || [];
  };

  const isFieldEnabled = (fieldName: string) => {
    if (!isContractorOrVendor) return false;
    return getEnabledFields().includes(fieldName);
  };

  const isFieldRequired = (fieldName: string) => {
    return getRequiredFields().includes(fieldName);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-3 h-auto p-1 bg-muted/50">
          <TabsTrigger 
            value="basic"
            className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-primary/90 data-[state=active]:text-primary-foreground data-[state=active]:shadow-md transition-all duration-200"
          >
            <Building2 className="h-4 w-4 mr-2" />
            Basic Info
          </TabsTrigger>
          <TabsTrigger 
            value="contractor" 
            disabled={!isContractorOrVendor}
            className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-primary/90 data-[state=active]:text-primary-foreground data-[state=active]:shadow-md transition-all duration-200"
          >
            <Wrench className="h-4 w-4 mr-2" />
            {companyType === 'contractor' ? 'Contractor' : companyType === 'vendor' ? 'Vendor' : 'Partner'} Details
          </TabsTrigger>
          <TabsTrigger 
            value="portal" 
            disabled={!isContractorOrVendor}
            className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-primary/90 data-[state=active]:text-primary-foreground data-[state=active]:shadow-md transition-all duration-200"
          >
            <Users className="h-4 w-4 mr-2" />
            Portal Access
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: Basic Information */}
        <TabsContent value="basic" className="space-y-6 mt-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-primary/5 via-primary/3 to-transparent py-3 px-4 rounded-lg border-l-4 border-l-primary/50">
              <h3 className="text-lg font-semibold text-foreground">Basic Information</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Company Name *</Label>
                <Input
                  id="name"
                  {...register("name", { required: "Company name is required" })}
                  placeholder="ABC Construction"
                />
                {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry">Industry</Label>
                <Input
                  id="industry"
                  {...register("industry")}
                  placeholder="Construction"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  {...register("website")}
                  placeholder="https://example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  {...register("email")}
                  placeholder="info@company.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  {...register("phone")}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Classification */}
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-accent/5 via-accent/3 to-transparent py-3 px-4 rounded-lg border-l-4 border-l-accent/50">
              <h3 className="text-lg font-semibold text-foreground">Classification</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_type">Company Type *</Label>
                <Select value={companyType} onValueChange={(value) => setValue('company_type', value as any)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="vendor">Vendor</SelectItem>
                    <SelectItem value="contractor">Contractor</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status *</Label>
                <Select value={status} onValueChange={(value) => setValue('status', value as any)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="relationship_tier">Relationship Tier</Label>
                <Select value={relationshipTier} onValueChange={(value) => setValue('relationship_tier', value as any)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vip">VIP</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="low_priority">Low Priority</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-primary/5 via-primary/3 to-transparent py-3 px-4 rounded-lg border-l-4 border-l-primary/50">
              <h3 className="text-lg font-semibold text-foreground">Address</h3>
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="address_line1">Address Line 1</Label>
                <Input
                  id="address_line1"
                  {...register("address_line1")}
                  placeholder="123 Main St"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address_line2">Address Line 2</Label>
                <Input
                  id="address_line2"
                  {...register("address_line2")}
                  placeholder="Suite 100"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    {...register("city")}
                    placeholder="New York"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    {...register("state")}
                    placeholder="NY"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="zip_code">Zip Code</Label>
                  <Input
                    id="zip_code"
                    {...register("zip_code")}
                    placeholder="10001"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    {...register("country")}
                    placeholder="USA"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Business Details */}
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-accent/5 via-accent/3 to-transparent py-3 px-4 rounded-lg border-l-4 border-l-accent/50">
              <h3 className="text-lg font-semibold text-foreground">Business Details</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="annual_revenue">Annual Revenue ($)</Label>
                <Input
                  id="annual_revenue"
                  type="number"
                  {...register("annual_revenue", { valueAsNumber: true })}
                  placeholder="1000000"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="employee_count">Employee Count</Label>
                <Input
                  id="employee_count"
                  type="number"
                  {...register("employee_count", { valueAsNumber: true })}
                  placeholder="50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="founded_year">Founded Year</Label>
                <Input
                  id="founded_year"
                  type="number"
                  {...register("founded_year", { valueAsNumber: true })}
                  placeholder="2000"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              {...register("notes")}
              placeholder="Additional notes about this company..."
              rows={4}
            />
          </div>
        </TabsContent>

        {/* TAB 2: Contractor/Vendor Details */}
        <TabsContent value="contractor" className="space-y-6 mt-4">
          {isContractorOrVendor && (
            <>
              {/* Prequalification Status */}
              {company && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="flex items-center gap-2">
                    <span>Prequalification Status:</span>
                    <PrequalificationStatusBadge
                      status={company.prequalification_status || 'not_started'}
                      expiryDate={company.prequalification_expiry_date}
                    />
                  </AlertDescription>
                </Alert>
              )}

              {/* Trade & Service Area */}
              {isFieldEnabled('trade_specialties') && (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-primary/5 via-primary/3 to-transparent py-3 px-4 rounded-lg border-l-4 border-l-primary/50">
                    <h3 className="text-lg font-semibold text-foreground">Trade & Service Area</h3>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Trade Specialties {isFieldRequired('trade_specialties') && <span className="text-destructive">*</span>}</Label>
                    <Controller
                      name="trade_specialties"
                      control={control}
                      rules={{ 
                        validate: (value) => {
                          if (isFieldRequired('trade_specialties') && (!value || value.length === 0)) {
                            return "At least one trade specialty is required";
                          }
                          return true;
                        }
                      }}
                      render={({ field }) => (
                        <TradeSpecialtiesSelector
                          value={field.value || []}
                          onChange={field.onChange}
                          required={isFieldRequired('trade_specialties')}
                        />
                      )}
                    />
                    {errors.trade_specialties && (
                      <p className="text-sm text-destructive">{errors.trade_specialties.message}</p>
                    )}
                  </div>

                  {isFieldEnabled('service_area_states') && (
                    <div className="space-y-2">
                      <Label htmlFor="service_area_radius_miles">Service Area Radius (miles)</Label>
                      <Input
                        id="service_area_radius_miles"
                        type="number"
                        {...register("service_area_radius_miles", { valueAsNumber: true })}
                        placeholder="50"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* License Information */}
              {isFieldEnabled('license_number') && (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-accent/5 via-accent/3 to-transparent py-3 px-4 rounded-lg border-l-4 border-l-accent/50">
                    <h3 className="text-lg font-semibold text-foreground">License Information</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="license_number">
                        License Number {isFieldRequired('license_number') && <span className="text-destructive">*</span>}
                      </Label>
                      <Input
                        id="license_number"
                        {...register("license_number", { required: isFieldRequired('license_number') })}
                        placeholder="CA-123456"
                      />
                    </div>

                    {isFieldEnabled('license_state') && (
                      <div className="space-y-2">
                        <Label htmlFor="license_state">
                          License State {isFieldRequired('license_state') && <span className="text-destructive">*</span>}
                        </Label>
                        <Input
                          id="license_state"
                          {...register("license_state")}
                          placeholder="CA"
                        />
                      </div>
                    )}

                    {isFieldEnabled('license_expiry_date') && (
                      <div className="space-y-2">
                        <Label htmlFor="license_expiry_date">License Expiry Date</Label>
                        <Input
                          id="license_expiry_date"
                          type="date"
                          {...register("license_expiry_date")}
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="license_type">License Type</Label>
                      <Input
                        id="license_type"
                        {...register("license_type")}
                        placeholder="General Contractor"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Insurance Information */}
              {isFieldEnabled('insurance_general_liability_amount') && (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-primary/5 via-primary/3 to-transparent py-3 px-4 rounded-lg border-l-4 border-l-primary/50">
                    <h3 className="text-lg font-semibold text-foreground">Insurance Information</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="insurance_general_liability_amount">
                        General Liability Amount ($) {isFieldRequired('insurance_general_liability_amount') && <span className="text-destructive">*</span>}
                      </Label>
                      <Input
                        id="insurance_general_liability_amount"
                        type="number"
                        {...register("insurance_general_liability_amount", { valueAsNumber: true })}
                        placeholder="1000000"
                      />
                    </div>

                    {isFieldEnabled('insurance_workers_comp_amount') && (
                      <div className="space-y-2">
                        <Label htmlFor="insurance_workers_comp_amount">Workers Comp Amount ($)</Label>
                        <Input
                          id="insurance_workers_comp_amount"
                          type="number"
                          {...register("insurance_workers_comp_amount", { valueAsNumber: true })}
                          placeholder="500000"
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="insurance_umbrella_amount">Umbrella Amount ($)</Label>
                      <Input
                        id="insurance_umbrella_amount"
                        type="number"
                        {...register("insurance_umbrella_amount", { valueAsNumber: true })}
                        placeholder="2000000"
                      />
                    </div>

                    {isFieldEnabled('insurance_expiry_date') && (
                      <div className="space-y-2">
                        <Label htmlFor="insurance_expiry_date">Insurance Expiry Date</Label>
                        <Input
                          id="insurance_expiry_date"
                          type="date"
                          {...register("insurance_expiry_date")}
                        />
                      </div>
                    )}

                    <div className="space-y-2 col-span-2">
                      <Label htmlFor="insurance_carrier">Insurance Carrier</Label>
                      <Input
                        id="insurance_carrier"
                        {...register("insurance_carrier")}
                        placeholder="State Farm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Bonding */}
              {isFieldEnabled('bond_capacity') && (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-accent/5 via-accent/3 to-transparent py-3 px-4 rounded-lg border-l-4 border-l-accent/50">
                    <h3 className="text-lg font-semibold text-foreground">Bonding</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="bond_capacity">Bond Capacity ($)</Label>
                      <Input
                        id="bond_capacity"
                        type="number"
                        {...register("bond_capacity", { valueAsNumber: true })}
                        placeholder="5000000"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bonding_company">Bonding Company</Label>
                      <Input
                        id="bonding_company"
                        {...register("bonding_company")}
                        placeholder="Liberty Mutual Surety"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Business Details */}
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-primary/5 via-primary/3 to-transparent py-3 px-4 rounded-lg border-l-4 border-l-primary/50">
                  <h3 className="text-lg font-semibold text-foreground">Additional Business Details</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="duns_number">DUNS Number</Label>
                    <Input
                      id="duns_number"
                      {...register("duns_number")}
                      placeholder="123456789"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="federal_tax_id">Federal Tax ID</Label>
                    <Input
                      id="federal_tax_id"
                      {...register("federal_tax_id")}
                      placeholder="12-3456789"
                    />
                  </div>

                  {isFieldEnabled('payment_terms') && (
                    <div className="space-y-2">
                      <Label htmlFor="payment_terms">Payment Terms</Label>
                      <Input
                        id="payment_terms"
                        {...register("payment_terms")}
                        placeholder="Net 30"
                      />
                    </div>
                  )}

                  {isFieldEnabled('typical_crew_size') && (
                    <div className="space-y-2">
                      <Label htmlFor="typical_crew_size">Typical Crew Size</Label>
                      <Input
                        id="typical_crew_size"
                        type="number"
                        {...register("typical_crew_size", { valueAsNumber: true })}
                        placeholder="5"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="years_in_business">Years in Business</Label>
                    <Input
                      id="years_in_business"
                      type="number"
                      {...register("years_in_business", { valueAsNumber: true })}
                      placeholder="10"
                    />
                  </div>
                </div>

                {isFieldEnabled('union_status') && (
                  <div className="pt-2">
                    <UnionStatusSelector
                      value={unionStatus}
                      onChange={(val) => setValue('union_status', val as any)}
                    />
                  </div>
                )}

                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox
                    id="is_mwbe_certified"
                    checked={isMwbeCertified}
                    onCheckedChange={(checked) => setValue('is_mwbe_certified', checked as boolean)}
                  />
                  <Label htmlFor="is_mwbe_certified" className="font-normal cursor-pointer">
                    MWBE Certified (Minority/Women Business Enterprise)
                  </Label>
                </div>
              </div>

              {/* Current Performance */}
              {company && (company.safety_rating || company.quality_rating || company.reliability_rating) && (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-success/5 via-success/3 to-transparent py-3 px-4 rounded-lg border-l-4 border-l-success/50">
                    <h3 className="text-lg font-semibold text-foreground">Current Performance Ratings</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                    {company.safety_rating && (
                      <div className="space-y-1">
                        <Label className="text-sm text-muted-foreground">Safety</Label>
                        <RatingDisplay rating={company.safety_rating} size="sm" />
                      </div>
                    )}
                    {company.quality_rating && (
                      <div className="space-y-1">
                        <Label className="text-sm text-muted-foreground">Quality</Label>
                        <RatingDisplay rating={company.quality_rating} size="sm" />
                      </div>
                    )}
                    {company.reliability_rating && (
                      <div className="space-y-1">
                        <Label className="text-sm text-muted-foreground">Reliability</Label>
                        <RatingDisplay rating={company.reliability_rating} size="sm" />
                      </div>
                    )}
                    {company.overall_rating && (
                      <div className="space-y-1">
                        <Label className="text-sm text-muted-foreground font-semibold">Overall</Label>
                        <RatingDisplay rating={company.overall_rating} size="md" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Internal Notes */}
              {isFieldEnabled('contractor_notes') && (
                <div className="space-y-2">
                  <Label htmlFor="contractor_notes">Internal Notes</Label>
                  <Textarea
                    id="contractor_notes"
                    {...register("contractor_notes")}
                    placeholder="Internal notes (not visible to contractor)..."
                    rows={4}
                  />
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* TAB 3: Portal Access */}
        <TabsContent value="portal" className="mt-4">
          {company && isContractorOrVendor && (
            <ContractorPortalUsersSection 
              companyId={company.id}
            />
          )}
          {!company && isContractorOrVendor && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Portal users can be added after creating the company.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>
      </Tabs>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : company ? "Update" : "Create"}
        </Button>
      </div>
    </form>
  );
}